import type { ReactNode } from 'react';
import { Button } from '@clay-run/glaze/button';
import { Badge } from '@clay-run/glaze/badge';
import type { EnrollmentItem } from '@shared/flow/enrollment';
import { contactDisplayIdentity } from '../../EnrollQueue/contactIdentity';
import { isItemOverridden } from '../../EnrollQueue/queue-group-send';

/**
 * The per-row Overridden badge + reset (decision 7). `null` for a row with
 * no config of its own, so the badge's presence IS the answer to "does this
 * contact have its own config?".
 *
 * D12 point 6 — "its own config" widened from copy to copy-OR-sender, and
 * the composition happens HERE rather than inside `isItemOverridden` (which
 * stays pure and server-derived): the sender half is the rep's own
 * browser-persisted `senderTouched` pin, which no `EnrollmentItem` field can
 * express — a seeded `sender` is indistinguishable in the DB from a
 * hand-picked one. `baseSender` is the effective play-level sender the reset
 * re-materializes; the caller computes it (`staged ?? default ?? null`)
 * because only it knows the play.
 *
 * Disabled while the item is mid-pipeline for the same reason Remove is
 * (`LeadRow`'s `itemBusy` guard): nulling the drafts under an in-flight
 * save/send would ship copy the rep just discarded.
 *
 * Used by `LeadExpandedBody` for both groupings' rows (plan U5 "no fork" rule).
 */
export function overriddenAccessory(
  item: EnrollmentItem,
  readOnly: boolean,
  itemBusy: boolean,
  onResetToBase: (itemId: number, baseSender: string | null) => Promise<void>,
  sender: { senderTouched: boolean; baseSender: string | null },
): ReactNode {
  if (!isItemOverridden(item) && !sender.senderTouched) return null;
  return (
    <span className="flex items-center gap-1.5">
      <Badge color="blue" size="xs">Overridden</Badge>
      <Button
        variant="ghost"
        size="xs"
        disabled={readOnly || itemBusy || item.status === 'sending' || item.status === 'queued'}
        aria-label={`Reset ${contactDisplayIdentity(item)} to the play copy`}
        onClick={() => { void onResetToBase(item.id, sender.baseSender); }}
      >
        Reset to base copy
      </Button>
    </span>
  );
}
